// Room builder — Heybase-style editor.
// Layout:
//   • Top bar: back, room name (editable), tabs (Activity / Editor / Preview), Share, Publish
//   • Left sidebar: logo + ordered section list + "Add new section"
//   • Main content: cover image (Edit cover) + rich section body (block editor)
//   • Right slide-out panel: Share (Guest / Team), Settings (Appearance)

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Share2, Settings2, X, Plus, Eye, Loader2, Check, Copy, Globe,
  MoreVertical, Trash2, ImageIcon, Type, AlignLeft, Paperclip, Video, Minus,
  Image as ImageIco, Activity as ActivityIcon, BookmarkPlus, Mail,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmModal";
import RichTextEditor from "../components/RichTextEditor";
import ProposalImageUpload from "../components/ProposalImageUpload";
import { AssetPickerModal } from "../components/AssetLibrary";

const BLOCK_MENU = [
  { type: "heading",     label: "Heading",   icon: Type,      hint: "Section headline" },
  { type: "rich_text",   label: "Text",      icon: AlignLeft, hint: "Paragraphs, lists" },
  { type: "image",       label: "Image",     icon: ImageIco,  hint: "Inline image" },
  { type: "video_embed", label: "Video",     icon: Video,     hint: "Loom, YouTube, Vimeo" },
  { type: "file",        label: "File",      icon: Paperclip, hint: "PDF attachment" },
  { type: "divider",     label: "Divider",   icon: Minus,     hint: "Section break" },
];

const fmtDate = (iso) => {
  if (!iso) return "Never";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

export default function RoomBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("editor");   // editor | preview | activity
  const [activeSection, setActiveSection] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [activity, setActivity] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rooms/${id}`);
      setRoom(data);
      if (!activeSection && data.sections?.length) setActiveSection(data.sections[0].id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load room");
      navigate("/rooms");
    } finally { setLoading(false); }
  }, [id, navigate, activeSection]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Load activity on demand
  useEffect(() => {
    if (tab !== "activity" || !room) return;
    api.get(`/rooms/${id}/activity`).then((r) => setActivity(r.data)).catch(() => {});
  }, [tab, id, room]);

  const patchRoom = async (updates) => {
    try {
      const { data } = await api.patch(`/rooms/${id}`, updates);
      setRoom(data);
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const saveName = async (name) => {
    setSavingName(true);
    await patchRoom({ name });
    setSavingName(false);
    setEditingName(false);
  };

  const addSection = async () => {
    try {
      const { data } = await api.post(`/rooms/${id}/sections`, { title: "New section" });
      const newRoom = { ...room, sections: [...(room.sections || []), { ...data, blocks: [] }] };
      setRoom(newRoom);
      setActiveSection(data.id);
    } catch (e) { toast.error("Failed"); }
  };

  const renameSection = async (section) => {
    const val = await confirm({
      title: "Rename section",
      confirmLabel: "Save",
      input: { label: "Section name", placeholder: section.title, required: true },
    });
    if (!val || val === section.title) return;
    try {
      await api.patch(`/rooms/sections/${section.id}`, { title: val });
      setRoom((r) => ({ ...r, sections: r.sections.map(s => s.id === section.id ? { ...s, title: val } : s) }));
    } catch { toast.error("Failed"); }
  };

  const deleteSection = async (section) => {
    if ((room?.sections || []).length <= 1) {
      toast.error("Rooms need at least one section");
      return;
    }
    const ok = await confirm({
      title: "Delete section?",
      message: `"${section.title}" and all its content will be removed.`,
      destructive: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/rooms/sections/${section.id}`);
      const remaining = room.sections.filter(s => s.id !== section.id);
      setRoom({ ...room, sections: remaining });
      if (activeSection === section.id) setActiveSection(remaining[0]?.id);
    } catch { toast.error("Failed"); }
  };

  const publish = async () => {
    try {
      const { data } = await api.post(`/rooms/${id}/publish`);
      await load();
      const url = `${window.location.origin}${data.public_path}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Room published — link copied");
      setShowShare(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Publish failed");
    }
  };

  const saveAsTemplate = async () => {
    const val = await confirm({
      title: "Save as template",
      message: "Anyone on your team can start a new room from this template.",
      confirmLabel: "Save template",
      input: { label: "Template name", placeholder: `${room?.name || "Room"} template`, required: true },
    });
    if (!val) return;
    try {
      await api.post(`/rooms/${id}/save-as-template`, { template_name: val });
      toast.success(`Saved "${val}" to your team template library`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  if (loading || !room) {
    return <div className="p-8 text-mute mono text-xs">loading…</div>;
  }

  const section = room.sections?.find((s) => s.id === activeSection);

  return (
    <div data-testid="room-builder-page" style={{ minHeight: "100vh", background: "var(--pg-bg)" }}>
      {/* Top bar */}
      <div style={{
        display: "flex", alignItems: "center", padding: "10px 20px",
        borderBottom: "1px solid var(--pg-border)", background: "var(--pg-surface)", gap: 12,
      }}>
        <button className="pg-btn pg-btn-ghost" onClick={() => navigate("/rooms")} data-testid="room-back">
          <ArrowLeft size={14} />
        </button>
        {editingName ? (
          <input
            autoFocus
            defaultValue={room.name}
            onBlur={(e) => saveName(e.target.value.trim() || "Untitled room")}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEditingName(false); }}
            className="pg-input"
            style={{ maxWidth: 320, fontSize: 14 }}
            data-testid="room-name-input"
          />
        ) : (
          <button
            onClick={() => setEditingName(true)}
            data-testid="room-name-display"
            style={{
              fontSize: 14, fontWeight: 500, color: "var(--pg-text)", background: "none",
              border: "none", cursor: "pointer", padding: "6px 10px", borderRadius: 6,
            }}
          >
            {savingName && <Loader2 size={12} className="animate-spin" />} {room.name}
          </button>
        )}
        <div style={{ flex: 1 }} />
        <div className="view-toggle-glass" style={{ margin: "0 auto" }}>
          <button className={`vt-btn ${tab === "activity" ? "active" : ""}`} onClick={() => setTab("activity")} data-testid="room-tab-activity">Activity</button>
          <button className={`vt-btn ${tab === "editor" ? "active" : ""}`} onClick={() => setTab("editor")} data-testid="room-tab-editor">Editor</button>
          <button className={`vt-btn ${tab === "preview" ? "active" : ""}`} onClick={() => setTab("preview")} data-testid="room-tab-preview">Preview</button>
        </div>
        <div style={{ flex: 1 }} />
        <button className="pg-btn pg-btn-ghost" onClick={() => setShowSettings(true)} data-testid="room-settings-btn">
          <Settings2 size={14} />
        </button>
        <button
          className="pg-btn pg-btn-ghost"
          onClick={saveAsTemplate}
          data-testid="room-save-template-btn"
          title="Save as template"
        >
          <BookmarkPlus size={14} />
        </button>
        {room.status === "published" ? (
          <span className="mono text-[11px]" style={{ color: "#059669", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Check size={12} /> Published
          </span>
        ) : (
          <button className="pg-btn pg-btn-primary" onClick={publish} data-testid="room-publish-btn">
            <Globe size={13} /> Publish
          </button>
        )}
        <button className="pg-btn" onClick={() => setShowShare(true)} data-testid="room-share-btn">
          <Share2 size={13} /> Share
        </button>
      </div>

      {/* Main layout */}
      {tab === "activity" ? (
        <ActivityView activity={activity} room={room} />
      ) : (
        <div style={{ display: "flex", minHeight: "calc(100vh - 56px)" }}>
          <SectionSidebar
            room={room}
            activeSection={activeSection}
            onSelect={setActiveSection}
            onAdd={addSection}
            onRename={renameSection}
            onDelete={deleteSection}
            readonly={tab === "preview"}
          />
          <div style={{ flex: 1, padding: "0 40px 60px", overflowX: "hidden", maxWidth: "100%" }}>
            {section && (
              <SectionCanvas
                key={section.id}
                room={room}
                section={section}
                readonly={tab === "preview"}
                onChanged={load}
              />
            )}
          </div>
        </div>
      )}

      {showShare && (
        <SharePanel room={room} onClose={() => setShowShare(false)} onPublish={publish} onPatch={patchRoom} />
      )}
      {showSettings && (
        <SettingsPanel room={room} onClose={() => setShowSettings(false)} onPatch={patchRoom} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Left sidebar — section list
// ─────────────────────────────────────────────────────────────────────────
function SectionSidebar({ room, activeSection, onSelect, onAdd, onRename, onDelete, readonly }) {
  const [openMenu, setOpenMenu] = useState(null);
  useEffect(() => {
    const close = () => setOpenMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  return (
    <aside
      data-testid="room-section-sidebar"
      style={{
        width: 240, flexShrink: 0, padding: "24px 12px",
        borderRight: "1px solid var(--pg-border)",
        background: "var(--pg-surface)",
      }}
    >
      {/* Logo circle */}
      <div style={{ padding: "0 8px 20px", display: "flex", justifyContent: "center" }}>
        <div
          style={{
            width: 60, height: 60, borderRadius: "50%",
            background: room.logo_url ? "transparent" : `linear-gradient(135deg, ${room.theme_color || "#5B4FE8"}, #8b7cff)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
          }}
        >
          {room.logo_url ? (
            <img src={room.logo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 22 }}>
              {(room.name || "R").slice(0, 1).toUpperCase()}
            </span>
          )}
        </div>
      </div>
      {(room.sections || []).map((s) => {
        const isActive = s.id === activeSection;
        return (
          <div
            key={s.id}
            style={{
              display: "flex", alignItems: "center",
              padding: "10px 12px", marginBottom: 4,
              borderRadius: 8, cursor: "pointer", position: "relative",
              background: isActive ? "var(--pg-accent-soft)" : "transparent",
              borderLeft: isActive ? `3px solid ${room.theme_color || "var(--pg-accent)"}` : "3px solid transparent",
              fontSize: 13, fontWeight: isActive ? 600 : 500,
              color: isActive ? "var(--pg-text)" : "var(--pg-text-mute)",
            }}
            onClick={() => onSelect(s.id)}
            data-testid={`section-item-${s.id}`}
          >
            <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {s.title}
            </span>
            {!readonly && isActive && (
              <button
                type="button"
                data-testid={`section-menu-${s.id}`}
                onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === s.id ? null : s.id); }}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "var(--pg-text-mute)" }}
              >
                <MoreVertical size={13} />
              </button>
            )}
            {openMenu === s.id && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute", top: "100%", right: 8, zIndex: 20,
                  background: "var(--pg-surface)", border: "1px solid var(--pg-border)",
                  borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                  padding: 4, marginTop: 4, width: 140,
                }}
              >
                <button
                  className="pg-btn pg-btn-ghost w-full"
                  onClick={() => { setOpenMenu(null); onRename(s); }}
                  data-testid={`section-rename-${s.id}`}
                  style={{ justifyContent: "flex-start", fontSize: 12 }}
                >
                  Rename
                </button>
                <button
                  className="pg-btn pg-btn-ghost w-full"
                  onClick={() => { setOpenMenu(null); onDelete(s); }}
                  data-testid={`section-delete-${s.id}`}
                  style={{ justifyContent: "flex-start", fontSize: 12, color: "#dc2626" }}
                >
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            )}
          </div>
        );
      })}
      {!readonly && (
        <button
          type="button"
          onClick={onAdd}
          data-testid="section-add-btn"
          className="pg-btn pg-btn-ghost w-full"
          style={{ justifyContent: "flex-start", marginTop: 8, fontSize: 12, color: "var(--pg-text-mute)" }}
        >
          <Plus size={13} /> Add new section
        </button>
      )}
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Main canvas — cover + blocks for the active section
// ─────────────────────────────────────────────────────────────────────────
function SectionCanvas({ room, section, readonly, onChanged }) {
  const [blocks, setBlocks] = useState(section.blocks || []);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);

  useEffect(() => { setBlocks(section.blocks || []); }, [section.id, section.blocks]);

  const addBlock = async (type) => {
    try {
      const { data } = await api.post(`/rooms/sections/${section.id}/blocks`, { type, content: {} });
      setBlocks((b) => [...b, data]);
    } catch { toast.error("Failed"); }
  };

  const updateBlock = async (block, content) => {
    setBlocks((b) => b.map(x => x.id === block.id ? { ...x, content } : x));
    try {
      await api.patch(`/rooms/blocks/${block.id}`, { content });
    } catch { toast.error("Save failed"); }
  };

  const deleteBlock = async (block) => {
    try {
      await api.delete(`/rooms/blocks/${block.id}`);
      setBlocks((b) => b.filter(x => x.id !== block.id));
    } catch { toast.error("Failed"); }
  };

  const setCover = async (url) => {
    try {
      await api.patch(`/rooms/${room.id}`, { cover_image_url: url });
      onChanged?.();
    } catch { toast.error("Failed"); }
  };

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", paddingTop: 24 }}>
      {/* Room cover image (per-room, appears on every section) */}
      <div
        data-testid="room-cover"
        style={{
          position: "relative", height: 220, borderRadius: 12, overflow: "hidden",
          background: room.cover_image_url ? undefined : `linear-gradient(135deg, ${room.theme_color || "#5B4FE8"}, #8b7cff)`,
          marginBottom: 26,
        }}
      >
        {room.cover_image_url && (
          <img src={room.cover_image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        )}
        {!readonly && (
          <button
            onClick={() => setCoverPickerOpen(true)}
            data-testid="room-edit-cover"
            style={{
              position: "absolute", top: 14, right: 14,
              padding: "6px 12px", fontSize: 12, fontWeight: 500,
              background: "rgba(15,23,42,0.65)", color: "#fff",
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6,
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
              backdropFilter: "blur(8px)",
            }}
          >
            <ImageIco size={12} /> Edit cover
          </button>
        )}
      </div>

      {/* Blocks */}
      <div data-testid={`section-blocks-${section.id}`}>
        {blocks.length === 0 && readonly && (
          <div className="text-sm" style={{ color: "var(--pg-text-mute)", padding: 24, textAlign: "center" }}>
            This section is empty.
          </div>
        )}
        {blocks.map((block) => (
          <BlockRenderer
            key={block.id}
            block={block}
            readonly={readonly}
            onChange={(c) => updateBlock(block, c)}
            onDelete={() => deleteBlock(block)}
          />
        ))}
        {!readonly && (
          <div style={{ marginTop: 22 }}>
            <div className="mono text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--pg-text-mute)" }}>Add a block</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
              {BLOCK_MENU.map((b) => {
                const Icon = b.icon;
                return (
                  <button
                    key={b.type}
                    type="button"
                    onClick={() => addBlock(b.type)}
                    data-testid={`add-block-${b.type}`}
                    className="pg-btn"
                    style={{
                      flexDirection: "column", padding: "10px 8px", justifyContent: "center",
                      alignItems: "center", gap: 4, fontSize: 12,
                    }}
                  >
                    <Icon size={16} /> {b.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <AssetPickerModal
        open={coverPickerOpen}
        onClose={() => setCoverPickerOpen(false)}
        accept="image"
        title="Pick a cover image"
        onPick={(a) => setCover(`${window.location.origin}${a.url}`)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Block renderer
// ─────────────────────────────────────────────────────────────────────────
function BlockRenderer({ block, readonly, onChange, onDelete }) {
  const c = block.content || {};
  const set = (patch) => onChange({ ...c, ...patch });

  return (
    <div
      data-testid={`block-${block.type}-${block.id}`}
      style={{
        position: "relative", padding: "10px 0", marginBottom: 6,
      }}
    >
      {!readonly && (
        <button
          type="button"
          onClick={onDelete}
          data-testid={`block-delete-${block.id}`}
          style={{
            position: "absolute", top: 12, right: -6, zIndex: 5,
            padding: 4, border: "1px solid var(--pg-border)", background: "var(--pg-surface)",
            borderRadius: 6, color: "var(--pg-text-mute)", cursor: "pointer",
            opacity: 0.5, transition: "opacity 140ms",
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
          onMouseLeave={(e) => e.currentTarget.style.opacity = 0.5}
          title="Delete block"
        >
          <Trash2 size={12} />
        </button>
      )}
      {block.type === "heading" && (
        <input
          value={c.text || ""}
          onChange={(e) => set({ text: e.target.value })}
          disabled={readonly}
          placeholder="Heading"
          className="pg-input"
          style={{
            border: "none", background: "transparent", padding: 0,
            fontSize: 26, fontWeight: 700, width: "100%",
            outline: "none",
          }}
        />
      )}
      {block.type === "rich_text" && (
        <RichTextEditor
          value={c.html || ""}
          onChange={(html) => set({ html })}
          disabled={readonly}
          placeholder="Type paragraphs here…"
          testId={`block-rich-${block.id}`}
        />
      )}
      {block.type === "image" && (
        <ProposalImageUpload
          value={{ url: c.url || "" }}
          onChange={({ url }) => set({ url })}
          kind="image"
          disabled={readonly}
          testId={`block-image-${block.id}`}
          height={140}
        />
      )}
      {block.type === "file" && (
        <ProposalImageUpload
          value={{ url: c.url || "", filename: c.label }}
          onChange={({ url, filename }) => set({ url, label: c.label || filename })}
          kind="file"
          disabled={readonly}
          testId={`block-file-${block.id}`}
          height={100}
        />
      )}
      {block.type === "video_embed" && (
        <VideoEmbedBlock c={c} set={set} readonly={readonly} bid={block.id} />
      )}
      {block.type === "divider" && (
        <hr style={{ border: 0, borderTop: "1px solid var(--pg-border)", margin: "18px 0" }} />
      )}
    </div>
  );
}

function VideoEmbedBlock({ c, set, readonly, bid }) {
  const url = c.url || "";
  const embed = toEmbedUrl(url);
  return (
    <div>
      {!readonly && (
        <input
          value={url}
          onChange={(e) => set({ url: e.target.value })}
          placeholder="Paste a Loom / YouTube / Vimeo link…"
          className="pg-input mb-2"
          data-testid={`block-video-url-${bid}`}
        />
      )}
      {embed ? (
        <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 10, overflow: "hidden", background: "#000" }}>
          <iframe
            src={embed}
            title="Video"
            allow="fullscreen; picture-in-picture; clipboard-write"
            frameBorder="0"
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
          />
        </div>
      ) : (
        <div style={{
          height: 140, background: "var(--pg-surface-alt, rgba(0,0,0,0.04))",
          border: "1px dashed var(--pg-border)", borderRadius: 10,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--pg-text-mute)", fontSize: 13,
        }}>
          <Video size={22} style={{ marginRight: 8 }} /> Paste a video link to embed
        </div>
      )}
    </div>
  );
}

function toEmbedUrl(raw) {
  if (!raw) return "";
  try {
    const u = new URL(raw);
    if (u.hostname.includes("loom.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      return `https://www.loom.com/embed/${id}`;
    }
    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }
    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      return `https://player.vimeo.com/video/${id}`;
    }
  } catch { /* invalid URL */ }
  return "";
}

// ─────────────────────────────────────────────────────────────────────────
// Share panel (right slide-out)
// ─────────────────────────────────────────────────────────────────────────
function SharePanel({ room, onClose, onPublish, onPatch }) {
  const isPublished = room.status === "published";
  const accessType = room.access_type || "public";
  const shareUrl = isPublished ? `${window.location.origin}/r/${room.share_token}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const setAccess = async (type) => {
    if (type === accessType) return;
    await onPatch?.({ access_type: type });
    toast.success(type === "email_gated" ? "Buyers will now need to enter their email" : "Anyone with the link can view");
  };
  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} data-testid="share-backdrop" />
      <div
        className="pg-card"
        data-testid="share-panel"
        style={{
          position: "fixed", top: 70, right: 16, bottom: 16,
          width: "min(380px, 92vw)", padding: 0, overflowY: "auto",
          zIndex: 200, borderRadius: 12,
        }}
      >
        <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--pg-border)" }}>
          <div style={{ display: "flex", gap: 6, fontSize: 13, fontWeight: 500 }}>
            <button className="pg-btn pg-btn-active" style={{ fontSize: 12 }}>Guest</button>
            <button className="pg-btn" style={{ fontSize: 12, opacity: 0.5 }} disabled>Team</button>
          </div>
          <button onClick={onClose} className="pg-btn pg-btn-ghost" style={{ padding: 5 }}><X size={14} /></button>
        </div>
        <div style={{ padding: 20 }}>
          <div className="mono text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--pg-text-mute)" }}>Access type</div>

          <button
            type="button"
            onClick={() => setAccess("email_gated")}
            data-testid="share-access-email"
            style={{
              width: "100%", textAlign: "left", padding: 14,
              border: accessType === "email_gated" ? "1px solid var(--pg-accent)" : "1px solid var(--pg-border)",
              borderRadius: 8,
              background: accessType === "email_gated" ? "var(--pg-accent-soft)" : "var(--pg-surface)",
              marginBottom: 10, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 10,
            }}
          >
            <Mail size={16} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Invite via Email</div>
              <div className="text-xs" style={{ color: "var(--pg-text-mute)" }}>Buyers must enter their email before entering</div>
            </div>
            {accessType === "email_gated" && <Check size={16} style={{ color: "var(--pg-accent)" }} />}
          </button>

          <button
            type="button"
            onClick={() => setAccess("public")}
            data-testid="share-access-public"
            style={{
              width: "100%", textAlign: "left", padding: 14,
              border: accessType === "public" ? "1px solid var(--pg-accent)" : "1px solid var(--pg-border)",
              borderRadius: 8,
              background: accessType === "public" ? "var(--pg-accent-soft)" : "var(--pg-surface)",
              marginBottom: 16, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 10,
            }}
          >
            <Globe size={16} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Public access</div>
              <div className="text-xs" style={{ color: "var(--pg-text-mute)" }}>Anyone with the link can view this room</div>
            </div>
            {accessType === "public" && <Check size={16} style={{ color: "var(--pg-accent)" }} />}
          </button>

          {isPublished ? (
            <>
              <div className="mono text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--pg-text-mute)" }}>Public room URL</div>
              <input
                readOnly
                value={shareUrl}
                onClick={(e) => e.target.select()}
                className="pg-input mono text-xs mb-3"
                data-testid="share-url-input"
              />
              <button className="pg-btn pg-btn-primary w-full" onClick={copy} data-testid="share-copy-btn">
                <Copy size={13} /> Copy link
              </button>
            </>
          ) : (
            <>
              <p className="text-sm mb-3" style={{ color: "var(--pg-text-mute)" }}>
                Publish this room to generate a public share URL.
              </p>
              <button className="pg-btn pg-btn-primary w-full" onClick={onPublish} data-testid="share-publish-btn">
                <Globe size={13} /> Publish room
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Appearance settings panel
// ─────────────────────────────────────────────────────────────────────────
const THEME_COLORS = [
  "#5B4FE8", "#dc2626", "#059669", "#f59e0b", "#0ea5e9",
  "#7c3aed", "#ec4899", "#ea580c", "#a16207", "#0f766e",
  "#334155", "#111827",
];

function SettingsPanel({ room, onClose, onPatch }) {
  const [color, setColor] = useState(room.theme_color || "#5B4FE8");
  const [layout, setLayout] = useState(room.layout || "classic");
  const [mode, setMode] = useState(room.theme_mode || "light");
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);

  const save = async (patch) => { await onPatch(patch); };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} data-testid="settings-backdrop" />
      <div
        className="pg-card"
        data-testid="room-settings-panel"
        style={{
          position: "fixed", top: 70, right: 16, bottom: 16,
          width: "min(400px, 92vw)", padding: 0, overflowY: "auto",
          zIndex: 200, borderRadius: 12,
        }}
      >
        <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--pg-border)" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Appearance</h3>
          <button onClick={onClose} className="pg-btn pg-btn-ghost" style={{ padding: 5 }}><X size={14} /></button>
        </div>
        <div style={{ padding: 20 }}>
          <div className="mono text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--pg-text-mute)" }}>Theme</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
            {[
              { k: "light", label: "Light" },
              { k: "dark", label: "Dark" },
              { k: "system", label: "System" },
            ].map((t) => (
              <button
                key={t.k}
                className={`pg-btn ${mode === t.k ? "pg-btn-active" : ""}`}
                onClick={() => { setMode(t.k); save({ theme_mode: t.k }); }}
                data-testid={`settings-theme-${t.k}`}
                style={{ flex: 1, fontSize: 12 }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="mono text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--pg-text-mute)" }}>Brand color</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6, marginBottom: 20 }}>
            {THEME_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { setColor(c); save({ theme_color: c }); }}
                data-testid={`settings-color-${c}`}
                style={{
                  height: 32, borderRadius: 6, cursor: "pointer",
                  background: c, border: color === c ? "2px solid var(--pg-text)" : "1px solid var(--pg-border)",
                }}
              />
            ))}
          </div>
          <input
            className="pg-input mono text-xs mb-4"
            placeholder="#HEX"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            onBlur={() => save({ theme_color: color })}
            data-testid="settings-color-hex"
          />

          <div className="mono text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--pg-text-mute)" }}>Layout</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
            {[
              { k: "modern", label: "Modern" },
              { k: "classic", label: "Classic" },
              { k: "one_pager", label: "One Pager" },
            ].map((l) => (
              <button
                key={l.k}
                className={`pg-btn ${layout === l.k ? "pg-btn-active" : ""}`}
                onClick={() => { setLayout(l.k); save({ layout: l.k }); }}
                data-testid={`settings-layout-${l.k}`}
                style={{ flex: 1, fontSize: 12 }}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="mono text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--pg-text-mute)" }}>Logo</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div
              style={{
                width: 48, height: 48, borderRadius: "50%",
                background: room.logo_url ? "transparent" : `linear-gradient(135deg, ${color}, #8b7cff)`,
                overflow: "hidden", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              {room.logo_url ? <img src={room.logo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> :
                <span style={{ color: "#fff", fontWeight: 700 }}>{(room.name || "R").slice(0, 1).toUpperCase()}</span>}
            </div>
            <button className="pg-btn" onClick={() => setLogoPickerOpen(true)} data-testid="settings-logo-pick">
              {room.logo_url ? "Replace logo" : "Choose logo"}
            </button>
            {room.logo_url && (
              <button
                className="pg-btn pg-btn-ghost"
                onClick={() => save({ logo_url: "" })}
                data-testid="settings-logo-clear"
                style={{ fontSize: 12 }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <AssetPickerModal
          open={logoPickerOpen}
          onClose={() => setLogoPickerOpen(false)}
          accept="image"
          title="Pick a logo"
          onPick={(a) => { save({ logo_url: `${window.location.origin}${a.url}` }); }}
        />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Activity view
// ─────────────────────────────────────────────────────────────────────────
function ActivityView({ activity, room }) {
  if (!activity) {
    return <div className="p-8 text-mute mono text-xs">loading activity…</div>;
  }
  const identified = activity.visitors || [];
  return (
    <div style={{ padding: "40px 60px", maxWidth: 900, margin: "0 auto" }} data-testid="room-activity">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 30 }}>
        <StatTile label="Total views" value={activity.views_total} />
        <StatTile label="Visitors" value={activity.visitors_total} />
        <StatTile label="Last opened" value={activity.last_viewed_at ? fmtDate(activity.last_viewed_at) : "Never"} small />
      </div>

      {activity.access_type === "email_gated" && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Visitors ({identified.length})</h3>
          {identified.length === 0 ? (
            <div className="pg-card" style={{ padding: 24, textAlign: "center", color: "var(--pg-text-mute)", marginBottom: 24 }}>
              No verified visitors yet. Buyers will appear here after submitting their email.
            </div>
          ) : (
            <div className="pg-card" style={{ marginBottom: 24 }}>
              {identified.map((v) => (
                <div key={v.visitor_id} data-testid={`activity-visitor-${v.visitor_id}`}
                     style={{ padding: "12px 16px", borderBottom: "1px solid var(--pg-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>{v.email}</div>
                    {v.name && <div className="text-xs" style={{ color: "var(--pg-text-mute)" }}>{v.name}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                    <div className="text-xs" style={{ color: "var(--pg-text-mute)" }}>
                      <span className="mono">{v.open_count}</span> opens · <span className="mono">{v.section_view_count}</span> sections
                    </div>
                    <div className="mono text-xs" style={{ color: "var(--pg-text-mute)", minWidth: 130, textAlign: "right" }}>
                      {fmtDate(v.last_seen_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Recent activity</h3>
      {(activity.views || []).length === 0 ? (
        <div className="pg-card" style={{ padding: 40, textAlign: "center", color: "var(--pg-text-mute)" }}>
          <ActivityIcon size={22} style={{ marginBottom: 10 }} /><br />
          No visitors yet. Share the room link to start seeing activity.
        </div>
      ) : (
        <div className="pg-card">
          {(activity.views || []).map((v) => (
            <div key={v.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--pg-border)", display: "flex", justifyContent: "space-between" }}>
              <div style={{ fontSize: 13 }}>
                {v.email ? (
                  <span style={{ fontWeight: 500 }}>{v.email}</span>
                ) : (
                  <span className="mono text-xs" style={{ color: "var(--pg-text-mute)" }}>{v.visitor_id?.slice(0, 8)}</span>
                )}
                <span style={{ marginLeft: 10, color: "var(--pg-text-mute)" }}>opened this room</span>
              </div>
              <div className="mono text-xs" style={{ color: "var(--pg-text-mute)" }}>{fmtDate(v.viewed_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, small }) {
  return (
    <div className="pg-card" style={{ padding: 18 }}>
      <div className="mono text-[10px] uppercase tracking-wider" style={{ color: "var(--pg-text-mute)" }}>{label}</div>
      <div style={{ fontSize: small ? 15 : 26, fontWeight: 600, marginTop: 6 }}>{value ?? "—"}</div>
    </div>
  );
}
